import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, ArrowUpRight } from "lucide-react";
import { Logo, EASE } from "./Reveal";
import { scrollToId } from "@/lib/api";
import { FAMILIES } from "@/lib/families";
import ThemeToggle from "./ThemeToggle";

const MENU = [
    { label: "Home", id: "home", testid: "home" },
    {
        label: "Products",
        testid: "products",
        children: [
            ...FAMILIES.map((f) => ({
                label: f.name,
                sub: f.tag,
                route: `/family/${f.slug}`,
                testid: `cat-${f.slug}`,
            })),
            { label: "Full Catalogue", sub: "Search & compare all families", id: "products", testid: "full-catalogue" },
        ],
    },
    {
        label: "Services",
        testid: "services",
        children: [
            { label: "Cable Prices & Estimate", sub: "Live rates, instant quotation", route: "/prices", testid: "prices" },
            { label: "Cable Cutting", sub: "Custom lengths from drums", route: "/cutting", testid: "cutting" },
            { label: "Upload BOQ", sub: "Instant quote from your BOQ", route: "/upload-boq", testid: "boq" },
            { label: "Cable Calculator", sub: "Size a cable by load", id: "calculator", testid: "calculator" },
        ],
    },
    {
        label: "Company",
        testid: "company",
        children: [
            { label: "About Us", sub: "40+ years in Bengaluru", id: "company", testid: "about" },
            { label: "Our Brands", sub: "Manufacturers we stock", id: "brands", testid: "brands" },
            { label: "Contact", sub: "Enquiry & directions", id: "enquiry", testid: "contact" },
        ],
    },
];

export default function Nav() {
    const [open, setOpen] = useState(false);
    const [drop, setDrop] = useState(null); // desktop dropdown label
    const [mobGroup, setMobGroup] = useState(null); // mobile accordion label
    const navigate = useNavigate();
    const dropTimer = useRef(null);

    const go = (l) => {
        setOpen(false);
        setDrop(null);
        setMobGroup(null);
        if (l.href) {
            window.open(l.href, "_blank", "noopener,noreferrer");
            return;
        }
        if (l.route) {
            setTimeout(() => navigate(l.route), open ? 250 : 0);
            return;
        }
        const run = () =>
            l.id === "home"
                ? window.__lenis
                    ? window.__lenis.scrollTo(0, { duration: 1.4 })
                    : window.scrollTo(0, 0)
                : scrollToId(l.id);
        setTimeout(run, open ? 250 : 0);
    };

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    useEffect(() => {
        const esc = (e) => e.key === "Escape" && setDrop(null);
        window.addEventListener("keydown", esc);
        return () => window.removeEventListener("keydown", esc);
    }, []);

    const openDrop = (label) => {
        clearTimeout(dropTimer.current);
        setDrop(label);
    };
    const closeDrop = () => {
        clearTimeout(dropTimer.current);
        dropTimer.current = setTimeout(() => setDrop(null), 150);
    };

    return (
        <>
            <header
                className="fixed top-0 inset-x-0 z-40 backdrop-blur-xl bg-paper/85 border-b border-line"
                data-testid="nav-header"
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-16 flex items-center justify-between gap-6">
                    <button
                        data-testid="nav-logo"
                        onClick={() => go({ id: "home" })}
                        className="flex items-center gap-3 shrink-0"
                    >
                        <Logo size={30} />
                        <span className="font-display font-bold tracking-[0.06em] text-xl text-ink">
                            Roxy Cables<span className="text-copper text-[10px] align-super ml-0.5">TM</span>
                        </span>
                    </button>

                    <nav className="hidden lg:flex items-center gap-5">
                        {MENU.map((l) =>
                            l.children ? (
                                <div
                                    key={l.label}
                                    className="relative"
                                    onMouseEnter={() => openDrop(l.label)}
                                    onMouseLeave={closeDrop}
                                >
                                    <button
                                        data-testid={`nav-dropdown-${l.testid}`}
                                        onClick={() => setDrop(drop === l.label ? null : l.label)}
                                        aria-expanded={drop === l.label}
                                        className={`flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-300 whitespace-nowrap ${
                                            drop === l.label ? "text-copper" : "text-mist hover:text-copper"
                                        }`}
                                    >
                                        {l.label}
                                        <ChevronDown
                                            size={12}
                                            className={`transition-transform duration-300 ${drop === l.label ? "rotate-180" : ""}`}
                                        />
                                    </button>
                                    <AnimatePresence>
                                        {drop === l.label && (
                                            <motion.div
                                                data-testid={`nav-menu-${l.testid}`}
                                                className={`absolute top-full pt-3 ${
                                                    l.testid === "company" ? "right-0" : "left-0"
                                                }`}
                                                initial={{ opacity: 0, y: 8 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: 8 }}
                                                transition={{ duration: 0.22, ease: EASE }}
                                            >
                                                <div className="w-72 rounded-md border border-line bg-paper shadow-xl py-2 max-h-[70vh] overflow-y-auto">
                                                    {l.children.map((c) => (
                                                        <button
                                                            key={c.testid}
                                                            data-testid={`nav-item-${c.testid}`}
                                                            onClick={() => go(c)}
                                                            className="group w-full text-left px-4 py-2.5 hover:bg-copper/10 transition-colors duration-200 flex items-center justify-between gap-3"
                                                        >
                                                            <span>
                                                                <span className="block text-sm font-medium text-ink group-hover:text-copper transition-colors duration-200">
                                                                    {c.label}
                                                                </span>
                                                                <span className="block font-mono text-[10px] text-dim tracking-wider mt-0.5">
                                                                    {c.sub}
                                                                </span>
                                                            </span>
                                                            <ArrowUpRight
                                                                size={14}
                                                                className="shrink-0 text-dim opacity-0 group-hover:opacity-100 group-hover:text-copper transition-all duration-200"
                                                            />
                                                        </button>
                                                    ))}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            ) : (
                                <button
                                    key={l.label}
                                    data-testid={`nav-link-${l.testid}`}
                                    onClick={() => go(l)}
                                    className="font-mono text-[10px] uppercase tracking-[0.18em] text-mist hover:text-copper transition-colors duration-300 whitespace-nowrap"
                                >
                                    {l.label}
                                </button>
                            )
                        )}
                    </nav>

                    <div className="hidden lg:flex items-center gap-5">
                        <ThemeToggle />
                        <span className="hidden xl:flex items-center gap-2 font-mono text-[11px] text-dim tracking-widest">
                            <Phone size={12} className="text-copper" />
                            <a href="tel:+919008177176" data-testid="nav-call-link" className="hover:text-copper transition-colors duration-300">+91 900 81 77 176</a>
                        </span>
                        <button
                            data-testid="nav-cta-button"
                            onClick={() => go({ id: "enquiry" })}
                            className="rounded-full bg-ink text-white font-mono uppercase tracking-[0.18em] text-[11px] px-6 py-2.5 hover:bg-copper transition-colors duration-300"
                        >
                            Request Quote
                        </button>
                    </div>

                    <div className="lg:hidden flex items-center gap-3">
                        <ThemeToggle />
                        <button
                            data-testid="nav-mobile-toggle"
                            className="text-ink p-2"
                            onClick={() => setOpen(!open)}
                            aria-label="Toggle menu"
                        >
                            {open ? <X size={24} /> : <Menu size={24} />}
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="nav-mobile-menu"
                        className="fixed inset-0 z-30 bg-paper flex flex-col justify-center px-8 overflow-y-auto py-20"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        {MENU.map((l, i) =>
                            l.children ? (
                                <motion.div
                                    key={l.label}
                                    className="border-b border-line"
                                    initial={{ opacity: 0, x: -32 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.08 + i * 0.07, duration: 0.5, ease: EASE }}
                                >
                                    <button
                                        data-testid={`nav-mobile-group-${l.testid}`}
                                        onClick={() => setMobGroup(mobGroup === l.label ? null : l.label)}
                                        aria-expanded={mobGroup === l.label}
                                        className="w-full flex items-center justify-between font-display font-bold text-3xl text-ink py-3"
                                    >
                                        <span>
                                            <span className="font-mono text-xs text-copper mr-4">0{i + 1}</span>
                                            {l.label}
                                        </span>
                                        <ChevronDown
                                            size={22}
                                            className={`text-copper transition-transform duration-300 ${
                                                mobGroup === l.label ? "rotate-180" : ""
                                            }`}
                                        />
                                    </button>
                                    <AnimatePresence>
                                        {mobGroup === l.label && (
                                            <motion.div
                                                data-testid={`nav-mobile-submenu-${l.testid}`}
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.3, ease: EASE }}
                                                className="overflow-hidden"
                                            >
                                                <div className="pb-4 pl-10 grid gap-1">
                                                    {l.children.map((c) => (
                                                        <button
                                                            key={c.testid}
                                                            data-testid={`nav-mobile-item-${c.testid}`}
                                                            onClick={() => go(c)}
                                                            className="text-left py-1.5 text-base text-mist hover:text-copper transition-colors duration-200"
                                                        >
                                                            {c.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                            ) : (
                                <motion.button
                                    key={l.label}
                                    data-testid={`nav-mobile-link-${l.testid}`}
                                    onClick={() => go(l)}
                                    className="text-left font-display font-bold text-3xl text-ink py-3 border-b border-line"
                                    initial={{ opacity: 0, x: -32 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.08 + i * 0.07, duration: 0.5, ease: EASE }}
                                >
                                    <span className="font-mono text-xs text-copper mr-4">0{i + 1}</span>
                                    {l.label}
                                </motion.button>
                            )
                        )}
                        <motion.button
                            data-testid="nav-mobile-cta"
                            onClick={() => go({ id: "enquiry" })}
                            className="mt-10 rounded-full bg-copper text-white font-mono uppercase tracking-[0.18em] text-sm px-8 py-4"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5, duration: 0.5, ease: EASE }}
                        >
                            Request Quote
                        </motion.button>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
